import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { bookingsAPI } from '../services/api';
import { formatCurrency, formatDate } from '../utils/currency';
import { 
  ArrowLeft, Calendar, TrendingUp, Wallet, CreditCard, 
  Clock, Building2, ChevronLeft, ChevronRight, RefreshCw 
} from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import useFinancialVisibility from '../hooks/useFinancialVisibility';

const MonthlyBookings = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { toast } = useToast();
  const hideFinancials = useFinancialVisibility();
  
  const [bookings, setBookings] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  
  // Parse month and venue from URL or use defaults
  const monthParam = searchParams.get('month');
  const venueParam = searchParams.get('venue');
  const [selectedVenue, setSelectedVenue] = useState(venueParam ? decodeURIComponent(venueParam) : null);
  const [currentDate, setCurrentDate] = useState(() => {
    if (monthParam) {
      const [year, month] = monthParam.split('-').map(Number);
      return new Date(year, month - 1, 1);
    }
    return new Date();
  });

  // Helper function to convert 24-hour time to 12-hour AM/PM format
  const formatTimeToAMPM = (time24) => {
    if (!time24 || time24 === '-') return '-';
    try {
      const [hours, minutes] = time24.split(':').map(Number);
      const period = hours >= 12 ? 'PM' : 'AM';
      const hours12 = hours % 12 || 12;
      return `${hours12}:${minutes.toString().padStart(2, '0')} ${period}`;
    } catch (e) {
      return time24;
    }
  };

  useEffect(() => {
    loadBookings();
  }, [currentDate]);

  const loadBookings = async () => {
    setIsLoading(true);
    try {
      const data = await bookingsAPI.getAll();
      const mappedBookings = data.map(booking => ({
        id: booking.id,
        clientName: booking.customerName,
        clientPhone: booking.customerPhone,
        venue: booking.venueName,
        venueId: booking.venueId,
        bookingType: booking.status?.toLowerCase() || 'confirmed',
        eventType: booking.eventType,
        eventStartDate: booking.eventDate,
        eventEndDate: booking.eventEndDate || booking.eventDate,
        startTime: booking.startTime,
        endTime: booking.endTime,
        guestCount: booking.guests,
        totalAmount: booking.amount,
        advanceAmount: booking.advancePaid || 0,
        payments: booking.payments || []
      }));
      
      // Filter out cancelled bookings to match CalendarView behavior
      setBookings(mappedBookings.filter(b => b.bookingType !== 'cancelled'));
    } catch (error) {
      console.error('Error loading bookings:', error);
      toast({
        title: 'Error',
        description: 'Failed to load bookings',
        variant: 'destructive'
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Helper function to parse date string to local date (avoiding timezone issues)
  const parseToLocalDate = (dateStr) => {
    if (!dateStr) return null;
    // Handle YYYY-MM-DD format - parse as local date, not UTC
    if (typeof dateStr === 'string' && dateStr.match(/^\d{4}-\d{2}-\d{2}$/)) {
      const [year, month, day] = dateStr.split('-').map(Number);
      return new Date(year, month - 1, day);
    }
    // For other formats, try to extract date parts
    const parsed = new Date(dateStr);
    if (!isNaN(parsed.getTime())) {
      return new Date(parsed.getFullYear(), parsed.getMonth(), parsed.getDate());
    }
    return null;
  };

  // Filter bookings for current month - based on START DATE only (commencement date)
  // Multi-day bookings are counted in the month when they START
  // Example: Feb 28 - Mar 1 booking is counted in February, not March
  // If selectedVenue is set, also filter by venue
  const monthBookings = bookings.filter(booking => {
    const startDate = parseToLocalDate(booking.eventStartDate);
    if (!startDate) return false;
    
    const targetMonth = currentDate.getMonth();
    const targetYear = currentDate.getFullYear();
    
    // Check if start date falls within the target month
    const dateMatch = startDate.getMonth() === targetMonth && startDate.getFullYear() === targetYear;
    
    // If venue filter is active, also check venue
    if (selectedVenue) {
      return dateMatch && booking.venue === selectedVenue;
    }
    
    return dateMatch;
  });

  // Calculate financials
  const financials = monthBookings.reduce((acc, booking) => {
    const amount = Number(booking.totalAmount) || 0;
    const advance = Number(booking.advanceAmount) || 0;
    const additionalPayments = (booking.payments || []).reduce((sum, p) => sum + Number(p.amount), 0);
    const totalPaid = advance + additionalPayments;
    
    acc.totalRevenue += amount;
    acc.totalPaid += totalPaid;
    acc.balance += (amount - totalPaid);
    
    // Event type breakdown
    const eventType = booking.eventType || 'Other';
    if (!acc.eventTypes[eventType]) {
      acc.eventTypes[eventType] = { count: 0, revenue: 0 };
    }
    acc.eventTypes[eventType].count += 1;
    acc.eventTypes[eventType].revenue += amount;
    
    return acc;
  }, { totalRevenue: 0, totalPaid: 0, balance: 0, eventTypes: {} });

  // Navigation
  const previousMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
  };

  const nextMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));
  };

  const monthYear = currentDate.toLocaleString('default', { month: 'long', year: 'numeric' });

  return (
    <div className="space-y-3 pb-20">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate('/calendar')}
            className="h-8 w-8"
          >
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <div>
            <h1 className="text-base font-bold text-gray-900 dark:text-foreground">Monthly Summary</h1>
            <p className="text-[10px] text-gray-500 dark:text-muted-foreground">
              {selectedVenue ? `${selectedVenue} • Bookings & Revenue` : 'All Venues • Bookings & Revenue'}
            </p>
          </div>
        </div>
        <Button 
          variant="outline" 
          size="icon"
          onClick={loadBookings}
          disabled={isLoading}
          className="h-8 w-8"
        >
          <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
        </Button>
      </div>

      {/* Month Navigation */}
      <div className="flex items-center justify-center gap-3 bg-gradient-to-r from-teal-500 to-emerald-600 text-white p-2 rounded-lg">
        <Button 
          variant="ghost" 
          size="icon" 
          onClick={previousMonth}
          className="h-7 w-7 text-white hover:bg-white dark:bg-card/20"
        >
          <ChevronLeft className="w-4 h-4" />
        </Button>
        <div className="text-center">
          <p className="text-sm font-bold">{monthYear}</p>
          <p className="text-[10px] opacity-80">{monthBookings.length} Bookings</p>
        </div>
        <Button 
          variant="ghost" 
          size="icon" 
          onClick={nextMonth}
          className="h-7 w-7 text-white hover:bg-white dark:bg-card/20"
        >
          <ChevronRight className="w-4 h-4" />
        </Button>
      </div>

      {/* Financial Summary */}
      {!hideFinancials && (
        <div className="space-y-2">
          {/* Revenue Cards */}
          <div className="grid grid-cols-3 gap-1.5">
            <Card className="border-0 shadow-sm bg-blue-50 dark:bg-blue-900/20">
              <CardContent className="p-2 text-center">
                <TrendingUp className="w-3.5 h-3.5 text-blue-600 mx-auto mb-0.5" />
                <p className="text-[8px] text-gray-600 dark:text-muted-foreground">Total Revenue</p>
                <p className="text-[11px] font-bold text-blue-700">
                  {formatCurrency(financials.totalRevenue)}
                </p>
              </CardContent>
            </Card>
            <Card className="border-0 shadow-sm bg-green-50 dark:bg-green-900/20">
              <CardContent className="p-2 text-center">
                <Wallet className="w-3.5 h-3.5 text-green-600 mx-auto mb-0.5" />
                <p className="text-[8px] text-gray-600 dark:text-muted-foreground">Received</p>
                <p className="text-[11px] font-bold text-green-700">
                  {formatCurrency(financials.totalPaid)}
                </p>
              </CardContent>
            </Card>
            <Card className="border-0 shadow-sm bg-orange-50 dark:bg-orange-900/20">
              <CardContent className="p-2 text-center">
                <CreditCard className="w-3.5 h-3.5 text-orange-600 mx-auto mb-0.5" />
                <p className="text-[8px] text-gray-600 dark:text-muted-foreground">Balance</p>
                <p className="text-[11px] font-bold text-orange-700">
                  {formatCurrency(financials.balance)}
                </p>
              </CardContent>
            </Card>
          </div>

          {/* Collection Progress */}
          <div className="bg-gray-50 dark:bg-secondary p-2 rounded-lg">
            <div className="flex justify-between text-[10px] mb-1">
              <span className="text-gray-600 dark:text-muted-foreground">Collection Progress</span>
              <span className="font-semibold">
                {financials.totalRevenue > 0 
                  ? Math.round((financials.totalPaid / financials.totalRevenue) * 100)
                  : 0}%
              </span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-1.5">
              <div 
                className="bg-gradient-to-r from-green-500 to-emerald-600 h-1.5 rounded-full"
                style={{ 
                  width: `${financials.totalRevenue > 0 
                    ? Math.min((financials.totalPaid / financials.totalRevenue) * 100, 100)
                    : 0}%` 
                }}
              ></div>
            </div>
          </div>

          {/* By Event Type */}
          {Object.keys(financials.eventTypes).length > 0 && (
            <div className="bg-white dark:bg-card rounded-lg border p-2">
              <p className="text-[10px] font-semibold text-gray-700 dark:text-foreground mb-1.5">By Event Type</p>
              <div className="space-y-1">
                {Object.entries(financials.eventTypes)
                  .sort((a, b) => b[1].revenue - a[1].revenue)
                  .map(([type, data]) => (
                    <div key={type} className="flex items-center justify-between text-[10px] py-0.5">
                      <div className="flex items-center gap-1.5">
                        <span className="w-1.5 h-1.5 rounded-full bg-teal-50 dark:bg-teal-900/200"></span>
                        <span className="text-gray-700 dark:text-foreground truncate max-w-[120px]">{type}</span>
                        <Badge variant="secondary" className="text-[8px] px-1 h-3.5">{data.count}</Badge>
                      </div>
                      <span className="font-semibold text-gray-900 dark:text-foreground">{formatCurrency(data.revenue)}</span>
                    </div>
                  ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* All Bookings */}
      <div>
        <h2 className="text-xs font-semibold text-gray-700 dark:text-foreground mb-1.5 flex items-center gap-1.5">
          <Calendar className="w-3.5 h-3.5 text-teal-600" />
          All Bookings
        </h2>
        
        {isLoading ? (
          <div className="text-center py-6">
            <RefreshCw className="w-5 h-5 animate-spin text-teal-500 mx-auto mb-1" />
            <p className="text-xs text-gray-500 dark:text-muted-foreground">Loading...</p>
          </div>
        ) : monthBookings.length > 0 ? (
          <div className="space-y-1.5">
            {monthBookings
              .sort((a, b) => new Date(a.eventStartDate) - new Date(b.eventStartDate))
              .map((booking) => {
                const totalPaid = (Number(booking.advanceAmount) || 0) + 
                  (booking.payments || []).reduce((sum, p) => sum + Number(p.amount), 0);
                const balance = (Number(booking.totalAmount) || 0) - totalPaid;
                const isCompleted = new Date(booking.eventEndDate || booking.eventStartDate) < new Date();
                
                return (
                  <Card 
                    key={booking.id} 
                    className={`border shadow-sm cursor-pointer ${
                      isCompleted ? 'bg-gray-50 dark:bg-secondary' : 'bg-white dark:bg-card'
                    }`}
                    onClick={() => navigate(`/bookings?highlight=${booking.id}`)}
                  >
                    <CardContent className="p-2">
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-1.5 mb-0.5">
                            <h3 className="font-medium text-xs text-gray-900 dark:text-foreground truncate">
                              {booking.clientName}
                            </h3>
                            <Badge 
                              className={`text-[7px] px-1 py-0 h-3.5 ${
                                booking.bookingType === 'blocked' 
                                  ? 'bg-yellow-400 text-yellow-900' 
                                  : isCompleted 
                                    ? 'bg-gray-400 text-white'
                                    : 'bg-teal-50 dark:bg-teal-900/200 text-white'
                              }`}
                            >
                              {booking.bookingType === 'blocked' ? 'Blocked' : isCompleted ? 'Done' : 'Confirmed'}
                            </Badge>
                          </div>
                          <p className="text-[9px] text-gray-500 dark:text-muted-foreground">{booking.eventType}</p>
                        </div>
                        {!hideFinancials && (
                          <div className="text-right flex-shrink-0">
                            <p className="text-[11px] font-semibold text-gray-900 dark:text-foreground">
                              {formatCurrency(booking.totalAmount || 0)}
                            </p>
                            {balance > 0 && (
                              <p className="text-[8px] text-orange-600">
                                Due: {formatCurrency(balance)}
                              </p>
                            )}
                          </div>
                        )}
                      </div>
                      
                      <div className="flex items-center gap-2 mt-1 text-[8px] text-gray-400 dark:text-muted-foreground flex-wrap">
                        <span className="flex items-center gap-0.5">
                          <Calendar className="w-2.5 h-2.5" />
                          {formatDate(booking.eventStartDate)}
                          {booking.eventEndDate && booking.eventEndDate !== booking.eventStartDate && (
                            <span> - {formatDate(booking.eventEndDate)}</span>
                          )}
                        </span>
                        <span className="flex items-center gap-0.5">
                          <Clock className="w-2.5 h-2.5" />
                          {formatTimeToAMPM(booking.startTime)}
                          {booking.endTime && (
                            <span> - {formatTimeToAMPM(booking.endTime)}</span>
                          )}
                        </span>
                        {booking.venue && (
                          <span className="flex items-center gap-0.5 truncate">
                            <Building2 className="w-2.5 h-2.5" />
                            <span className="truncate max-w-[70px]">{booking.venue}</span>
                          </span>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
          </div>
        ) : (
          <div className="text-center py-6 bg-gray-50 dark:bg-secondary rounded-lg">
            <Calendar className="w-8 h-8 text-gray-300 mx-auto mb-1" />
            <p className="text-xs text-gray-500 dark:text-muted-foreground">No bookings for {monthYear}</p>
            <Button 
              variant="outline" 
              size="sm" 
              className="mt-2 h-7 text-xs"
              onClick={() => navigate('/bookings')}
            >
              View All
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default MonthlyBookings;
